import type { NodeWithPos } from '@tiptap/core';
import { Fragment, NodeRange, type NodeType, Slice } from '@tiptap/pm/model';
import type { Selection, Transaction } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { liftTarget, ReplaceAroundStep } from '@tiptap/pm/transform';

import type { ChangeListItemsIndent } from './commons';
import { fixTextSelection } from './commons';

export const lifListItemNodes: ChangeListItemsIndent = (tr, siblingListItems, parentList) => {
  const parentListOffset = parentList.node.attrs.offset;
  const parentListStart = parentList.pos;

  const firstSibling = siblingListItems[0];
  const lastSibling = siblingListItems[siblingListItems.length - 1];
  const siblingsRangeStart = firstSibling.pos;
  const siblingsRangeEnd = lastSibling.pos + lastSibling.node.nodeSize;

  const shouldFirstListItemBeLifted =
    firstSibling.node.attrs.id === parentList.node.firstChild?.attrs.id;

  if (parentListOffset === 0) {
    semanticallyLiftListItems(firstSibling.node.type, tr, siblingsRangeStart, siblingsRangeEnd);
    return true;
  }

  if (shouldFirstListItemBeLifted) {
    tr.setNodeAttribute(parentListStart, 'offset', parentListOffset - 1);
    return false;
  }

  decreaseOffsetWithNewParent(tr, siblingListItems, parentList);
  return false;
};

const decreaseOffsetWithNewParent = (
  tr: Transaction,
  siblingListItems: NodeWithPos[],
  parentList: NodeWithPos
) => {
  const parentListOffset = parentList.node.attrs.offset;
  const parentListStart = parentList.pos;
  const parentListEnd = parentListStart + parentList.node.nodeSize;

  const siblingsRangeStart = siblingListItems[0].pos;
  const newListNode = parentList.node.cut(siblingsRangeStart - parentList.pos);

  const sizeOfContent = newListNode.nodeSize - 2;
  const parentEndWithoutContent = parentListEnd - sizeOfContent;

  const insertFragment = Fragment.from(
    newListNode.type.create(
      {
        ...newListNode.attrs,
        offset: parentListOffset - 1,
      },
      newListNode.content
    )
  );

  const preChangeSelection = tr.selection;
  tr.delete(siblingsRangeStart, parentListEnd - 1).insert(parentEndWithoutContent, insertFragment);

  fixTextSelection(tr, preChangeSelection);
  return false;
};

// this function is a copied from ProseMirror's liftListItem, except for changing the selection
// https://github.com/ProseMirror/prosemirror-schema-list/blob/79b62755a061869e0dfa1e877b5033f341dc0592/src/schema-list.ts#L173
const semanticallyLiftListItems = (
  itemType: NodeType,
  tr: Transaction,
  selectionStart: number,
  selectionEnd: number
) => {
  const newSelection = TextSelection.between(
    tr.doc.resolve(selectionStart),
    tr.doc.resolve(selectionEnd)
  );

  const { $from } = newSelection;

  const range = getBlockRange(newSelection, itemType);
  if (!range) {
    return false;
  }
  if ($from.node(range.depth - 1).type === itemType) {
    // Inside a parent list
    return liftToOuterList(tr, itemType, range);
  }
};
// this function is copied from ProseMirror's liftToOuterList with some minor changes
// https://github.com/ProseMirror/prosemirror-schema-list/blob/79b62755a061869e0dfa1e877b5033f341dc0592/src/schema-list.ts#L186
const liftToOuterList = (tr: Transaction, itemType: NodeType, range: NodeRange) => {
  const end = range.end;
  const endOfList = range.$to.end(range.depth);

  if (end < endOfList) {
    // There are siblings after the lifted items, which must become
    // children of the last item
    tr.step(
      new ReplaceAroundStep(
        end - 1,
        endOfList,
        end,
        endOfList,
        new Slice(Fragment.from(itemType.create(null, range.parent.copy())), 1, 0),
        1,
        true
      )
    );
    // eslint-disable-next-line no-param-reassign
    range = new NodeRange(tr.doc.resolve(range.$from.pos), tr.doc.resolve(endOfList), range.depth);
  }
  const target = liftTarget(range);
  if (target === null) {
    return false;
  }
  tr.lift(range, target);

  tr.scrollIntoView();
  return true;
};

const getBlockRange = (selection: Selection, itemType: NodeType) => {
  const { $from, $to } = selection;
  const range = $from.blockRange(
    $to,
    node => node.childCount > 0 && node.firstChild?.type === itemType
  );
  return range;
};
